import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AppWindow, BarChart3, Workflow, Cable, Clock, ArrowRight } from 'lucide-react';

const OFFER_ICONS: Record<string, React.ElementType> = {
  app: AppWindow,
  dashboard: BarChart3,
  automation: Workflow,
  integration: Cable
};

type Offer = {
  code: string;
  name: string;
  tagline: string;
  timeline: string;
};

export const ServicesTeaser = () => {
  const { t } = useTranslation();
  const offers = (t('services.offers.items', { returnObjects: true }) as Offer[]) || [];

  return (
    <section className="py-20 relative bg-slate-50 dark:bg-gray-800/40 border-y border-slate-100 dark:border-gray-800">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto"
        >
          <h2 className="text-4xl font-semibold text-slate-900 dark:text-gray-100">{t('services.offers.title')}</h2>
          <p className="mt-3 text-slate-600 dark:text-gray-300">{t('services.offers.subtitle')}</p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {offers.map((offer, index) => {
            const Icon = OFFER_ICONS[offer.code] || AppWindow;
            return (
              <motion.div
                key={offer.code}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <Link
                  to="/solutions"
                  className="group flex flex-col h-full rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-sky-300 dark:hover:border-cyan-700 transition-all"
                >
                  <div className="w-11 h-11 rounded-xl grid place-items-center bg-sky-50 dark:bg-cyan-900/30 text-sky-600 dark:text-cyan-400 border border-sky-100 dark:border-cyan-800">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-gray-100">{offer.name}</h3>
                  <p className="mt-2 text-sm text-slate-600 dark:text-gray-300 leading-relaxed flex-1">{offer.tagline}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-gray-400">
                      <Clock className="w-3.5 h-3.5" />
                      {offer.timeline}
                    </span>
                    <ArrowRight className="w-4 h-4 text-sky-600 dark:text-cyan-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
